import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { HiChartBar } from 'react-icons/hi';

const TheNumbers = () => {
  const [displayValues, setDisplayValues] = useState([0, 0, 0, 0]);
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  const stats = useMemo(() => [
    {
      targetValue: 42,
      label: 'Boost in conversions',
    },
    {
      targetValue: 70,
      label: 'More email clicks and opens',
    },
    {
      targetValue: 94,
      label: 'Time saved vs. traditional production',
    },
    {
      targetValue: 100,
      label: 'Optimized for TV, OTT, and digital platforms',
    },
  ], []);

  const animateNumbers = useCallback(() => {
    stats.forEach((stat, index) => {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const increment = stat.targetValue / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const currentValue = Math.min(
          Math.round(increment * currentStep),
          stat.targetValue
        );

        setDisplayValues((prev) => {
          const newValues = [...prev];
          newValues[index] = currentValue;
          return newValues;
        });

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  }, [stats]);

  useEffect(() => {
    const sectionElement = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Trigger animation when component enters viewport (even slightly visible)
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            // Small delay to ensure smooth animation start
            setTimeout(() => {
              animateNumbers();
            }, 100);
          }
        });
      },
      { 
        threshold: 0.1, // Trigger when 10% of component is visible (earlier trigger)
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully in view
      }
    );

    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, [animateNumbers]);

  const lineColors = ['bg-ink/15', 'bg-brand', 'bg-ink/15'];

  return (
    <section ref={sectionRef} className="bg-surface-alt py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Label */}
        <div className="flex items-center gap-2 mb-4">
          <HiChartBar className="text-brand text-sm md:text-base" />
          <h3 className="text-brand text-sm md:text-base font-semibold uppercase tracking-wide">
            OUR STATS
          </h3>
        </div>

        {/* Main Title */}
        <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-ink mb-12 md:mb-16">
          The Numbers
        </h2>

        {/* Statistics Grid */}
        <div className="relative">
          {/* Left Vertical Line */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-brand"></div>

          {/* Stats Container */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 relative">
            {stats.map((stat, index) => (
              <div key={index} className="relative text-center">
                {/* Vertical Separator Lines */}
                {index < stats.length - 1 && (
                  <div
                    className={`hidden lg:block absolute -right-6 top-0 bottom-0 w-0.5 ${lineColors[index]}`}
                  ></div>
                )}

                {/* Stat Value */}
                <div className="mb-4">
                  <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-ink">
                    {displayValues[index]}%
                  </p>
                </div>

                {/* Stat Label */}
                <p className="text-ink-muted text-sm sm:text-base md:text-lg leading-relaxed break-words px-2">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          {/* Right Vertical Line */}
          <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-brand"></div>
        </div>
      </div>
    </section>
  );
};

export default TheNumbers;

